import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useReducer,
} from 'react';

import type {BandOrNull} from '@modules/ai/band';
import type {PlaylistSummary} from '@modules/playlists/types';

export enum ImportPhase {
	IDLE = 'idle',
	IMPORTING = 'importing',
	ANALYZING = 'analyzing',
	DONE = 'done',
	ERROR = 'error',
}

/**
 * Wire shape per playlist. `band: null` = insufficient data. `hasAnyAi`
 * is a coarse boolean (≥1 AI track) used to nudge the band-0 ring so
 * "exactly 0%" reads differently from "≤1%" without leaking the ratio.
 */
export type PlaylistAnalysis = {
	playlistId: string;
	band: BandOrNull;
	hasAnyAi: boolean;
};

type State = {
	phase: ImportPhase;
	transferId: string | null;
	playlists: PlaylistSummary[];
	analyses: PlaylistAnalysis[];
	globalBand: BandOrNull;
	globalAiPercent: number | null;
	importProgress: ImportProgress | null;
	error: string | null;
};

export type ImportProgress = {
	/** 0..100 from TMM "in progress" payload. */
	percent: number;
	/** Seconds left from TMM payload, may be 0 when missing/unknown. */
	estimatedSecondsLeft: number;
};

type Action =
	| {type: 'START_IMPORT'; transferId: string}
	| {type: 'SET_IMPORT_PROGRESS'; progress: ImportProgress}
	| {type: 'SET_PLAYLISTS'; playlists: PlaylistSummary[]}
	| {
			type: 'SET_ANALYSIS_RESULT';
			analyses: PlaylistAnalysis[];
			globalBand: BandOrNull;
			globalAiPercent: number | null;
	  }
	| {type: 'FAIL'; message: string}
	| {type: 'RESET'};

const INITIAL_STATE: State = {
	phase: ImportPhase.IDLE,
	transferId: null,
	playlists: [],
	analyses: [],
	globalBand: null,
	globalAiPercent: null,
	importProgress: null,
	error: null,
};

export const importStateReducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'START_IMPORT':
			return {
				...INITIAL_STATE,
				phase: ImportPhase.IMPORTING,
				transferId: action.transferId,
			};
		case 'SET_IMPORT_PROGRESS':
			return {
				...state,
				importProgress: action.progress,
			};
		case 'SET_PLAYLISTS':
			return {
				...state,
				phase: ImportPhase.ANALYZING,
				playlists: action.playlists,
			};
		case 'SET_ANALYSIS_RESULT':
			return {
				...state,
				phase: ImportPhase.DONE,
				analyses: action.analyses,
				globalBand: action.globalBand,
				globalAiPercent: action.globalAiPercent,
			};
		case 'FAIL':
			return {
				...state,
				phase: ImportPhase.ERROR,
				error: action.message,
			};
		case 'RESET':
			return INITIAL_STATE;
		default: {
			const _exhaustive: never = action;
			return _exhaustive;
		}
	}
};

export type ImportStateValue = State & {
	startImport: (transferId: string) => void;
	setImportProgress: (progress: ImportProgress) => void;
	setPlaylists: (playlists: PlaylistSummary[]) => void;
	setAnalysisResult: (
		analyses: PlaylistAnalysis[],
		globalBand: BandOrNull,
		globalAiPercent: number | null,
	) => void;
	fail: (message: string) => void;
	reset: () => void;
};

const ImportStateContext = createContext<ImportStateValue | null>(null);

export const ImportStateProvider = ({
	children,
}: {
	children?: React.ReactNode;
}) => {
	const [state, dispatch] = useReducer(importStateReducer, INITIAL_STATE);

	const startImport = useCallback((transferId: string) => {
		dispatch({type: 'START_IMPORT', transferId});
	}, []);

	const setImportProgress = useCallback((progress: ImportProgress) => {
		dispatch({type: 'SET_IMPORT_PROGRESS', progress});
	}, []);

	const setPlaylists = useCallback((playlists: PlaylistSummary[]) => {
		dispatch({type: 'SET_PLAYLISTS', playlists});
	}, []);

	const setAnalysisResult = useCallback(
		(
			analyses: PlaylistAnalysis[],
			globalBand: BandOrNull,
			globalAiPercent: number | null,
		) => {
			dispatch({
				type: 'SET_ANALYSIS_RESULT',
				analyses,
				globalBand,
				globalAiPercent,
			});
		},
		[],
	);

	const fail = useCallback((message: string) => {
		dispatch({type: 'FAIL', message});
	}, []);

	const reset = useCallback(() => {
		dispatch({type: 'RESET'});
	}, []);

	const value = useMemo<ImportStateValue>(
		() => ({
			...state,
			startImport,
			setImportProgress,
			setPlaylists,
			setAnalysisResult,
			fail,
			reset,
		}),
		[
			state,
			startImport,
			setImportProgress,
			setPlaylists,
			setAnalysisResult,
			fail,
			reset,
		],
	);

	return (
		<ImportStateContext.Provider value={value}>
			{children}
		</ImportStateContext.Provider>
	);
};

export function useImportState(): ImportStateValue {
	const ctx = useContext(ImportStateContext);
	if (!ctx) {
		throw new Error(
			'useImportState must be used within an ImportStateProvider',
		);
	}
	return ctx;
}
